/**
 * Clarity Route Tracking for React SPA
 * 
 * Since Clarity doesn't automatically detect route changes in SPAs,
 * this module provides a hook to manually track route changes.
 * 
 * Usage in your root component or router setup:
 * ```tsx
 * import { useClarityRouteTracking } from './utils/clarityRouteTracking'
 * 
 * function App() {
 *   useClarityRouteTracking()
 *   // ... rest of your app
 * }
 * ```
 */

import { useContext, useEffect } from "react"
import { useLocation } from "react-router-dom"
import clarity from "@microsoft/clarity"
import { shouldTrackInClarity } from "./clarity"
import { User } from "../modules/mp-database"
import { FeatureFlagContext } from "../modules/mp-core/FeatureFlagProvider/FeatureFlagProvider"
import { FeatureFlagName } from "../modules/mp-utils/generic-utils/feature-flags/types"

/**
 * Custom hook to track route changes in Clarity.
 * Call this once in your root App component after user authentication.
 * 
 * This sends the clean pathname (e.g., /reporting/content/content-library) 
 * as the customPageId to Clarity, grouping all activity by URL path instead of 
 * creating hundreds of unique "pages" for every filter combination.
 * 
 * Only tracks if user role is eligible (teachers only) and in production.
 * 
 * @param user - The current user object
 */
export const useClarityRouteTracking = (user?: User) => {
  const location = useLocation()
  const { featureFlags } = useContext(FeatureFlagContext)

  useEffect(() => {
    const clarityEnabled = featureFlags[FeatureFlagName.enable_microsoft_clarity]
    if (!shouldTrackInClarity(user?.role, clarityEnabled)) {
      return
    }

    try {
      // Guard against calling Clarity APIs before clarity.init() has run.
      // App.jsx calls initClarity() after an async feature flag HTTP request, which can
      // lose the race against this hook firing via FeatureFlagContext. Without init(),
      // window.clarity is undefined and all Clarity calls throw.
      if (typeof window.clarity !== "function") {
        return
      }

      // Get clean pathname without query params
      const pathname = location.pathname

      // Update Clarity with the current page using the identify API
      // customPageId helps group sessions by page in the Clarity dashboard
      // Per Clarity docs: "For optimal user tracking, the Identify API should be 
      // called for each page of the website"
      const friendlyName = user ? `${user.firstName} ${user.lastName}` : undefined
      clarity.identify(user?.id, undefined, pathname, friendlyName)

      // Optional: Track query params as separate custom event
      // This keeps the main page analytics clean while still capturing filter usage
      if (location.search) {
        const params = new URLSearchParams(location.search)
        const filters: Record<string, string[]> = {}
        
        // Handle multiple values for the same key (e.g., ?test=EACT&test=CLT)
        params.forEach((value, key) => {
          if (filters[key]) {
            filters[key].push(value)
          } else {
            filters[key] = [value]
          }
        })

        // Send a custom event with filter data
        // This appears in the Smart events section of Clarity
        clarity.event("page_filters")
        
        // Add filter details as custom tags for better filtering in Clarity dashboard
        Object.entries(filters).forEach(([key, values]) => {
          clarity.setTag(`filter_${key}`, values.join(", "))
        })
      }

    } catch (error) {
      console.error("[Clarity] Route tracking error:", error)
    }
  }, [location, user, featureFlags])
}

/**
 * Alternative: Manual route tracking function if you prefer not to use the hook.
 * Call this whenever you want to manually track a route change.
 * 
 * Only tracks if user role is eligible (teachers only) and in production.
 * 
 * @param pathname - The route pathname (e.g., /reporting/content/content-library)
 * @param user - The current user object (optional, will skip tracking if not eligible)
 * @param featureFlagEnabled - The enable_microsoft_clarity feature flag value. Caller is
 *   responsible for sourcing this (e.g. from context or checkFeatureFlag) since this is a
 *   plain function and cannot use useContext directly.
 * @param filters - Optional query params or filters as an object (supports arrays for multiple values)
 */
export const trackClarityRoute = (
  pathname: string,
  user: User | undefined,
  featureFlagEnabled: boolean,
  filters?: Record<string, string | string[]>
) => {
  if (!shouldTrackInClarity(user?.role, featureFlagEnabled)) {
    return
  }

  try {
    if (typeof window.clarity !== "function") {
      return
    }

    // Update page identifier with user information
    const friendlyName = user ? `${user.firstName} ${user.lastName}` : undefined
    clarity.identify(user?.id, undefined, pathname, friendlyName)

    // Track filters if provided
    if (filters && Object.keys(filters).length > 0) {
      clarity.event("page_filters")
      
      Object.entries(filters).forEach(([key, value]) => {
        const tagValue = Array.isArray(value) ? value.join(", ") : value
        clarity.setTag(`filter_${key}`, tagValue)
      })
    }

  } catch (error) {
    console.error("[Clarity] Route tracking error:", error)
  }
}
